/**
 * Profile-picture upload control for the user settings Personal section.
 *
 * Shows the user's current avatar (uploaded image, else Gravatar, else
 * initials via UserAvatar) and lets them upload/replace or clear it. The
 * avatar is stored as a customuser/profile-photo BinaryFile via the
 * dedicated user avatar action, so this control commits immediately and
 * asks the parent to reload the settings snapshot afterward.
 */

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadIcon from '@mui/icons-material/Upload';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { deleteUserAvatar, uploadUserAvatar } from '@sophocles/api/user';
import UserAvatar from '@sophocles/components/UserAvatar';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

// Backend caps the upload at 5 MB for customuser/profile-photo; mirror it
// client-side so the user gets an instant rejection instead of a round trip.
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

export default function UserAvatarField({ userId, profile, disabled, onChanged }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const hasAvatar = Boolean(profile?.avatar);

  const handlePick = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async event => {
    const file = event.target.files?.[0];
    // Reset the input so picking the same file again re-fires onChange.
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_AVATAR_BYTES) {
      setError(t('userSettings.v2.avatarTooLarge', 'Image must be no more than 5 MB in size.'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await uploadUserAvatar(userId, file);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.file?.[0] || t('userSettings.v2.avatarUploadFailed', 'Failed to upload picture.'));
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteUserAvatar(userId);
      onChanged?.();
    } catch (err) {
      setError(t('userSettings.v2.avatarClearFailed', 'Failed to remove picture.'));
    } finally {
      setBusy(false);
    }
  };

  const handleClearClick = () => {
    confirmDelete(profile, { entityLabel: t('userSettings.profilePicture'), onConfirm: handleClear });
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} alignItems="center">
        <UserAvatar
          email={profile?.email}
          firstName={profile?.first_name}
          lastName={profile?.last_name}
          avatarUrl={profile?.avatar}
          size={64}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={busy ? <CircularProgress size={16} /> : <UploadIcon />}
            onClick={handlePick}
            disabled={disabled || busy}
          >
            {hasAvatar ? t('userSettings.v2.replaceAvatar', 'Replace') : t('userSettings.v2.uploadAvatar', 'Upload')}
          </Button>
          {hasAvatar ? (
            <Button
              variant="text"
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleClearClick}
              disabled={disabled || busy}
            >
              {t('actions.clear', 'Clear')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        sx={{ display: 'none' }}
      />
      {deleteConfirmDialog}
    </Stack>
  );
}

UserAvatarField.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  profile: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    avatar: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  onChanged: PropTypes.func,
};

UserAvatarField.defaultProps = {
  profile: null,
  disabled: false,
  onChanged: undefined,
};
